"use client";

import { Core } from "@faire/design-tokens";
import { ICON_SIZE_SIZES_PX_UNITLESS } from "@faire/web/slate/icons/lib/consts";
import {
  isPx,
  isRem,
  pxOrRemToUnitlessPx,
} from "@faire/web/slate/icons/lib/sizeUnitUtils";
import { SVGIconV2 } from "@faire/web/slate/icons/lib/SVGIconV2";
import { SVGIconPropsV2 } from "@faire/web/slate/icons/lib/types";
import { isIconSize } from "@faire/web/slate/icons/lib/utils";
import { legacyIconCls } from "@faire/web/slate/utils/tracking";
import * as React from "react";

type Direction = "up" | "down" | "left" | "right" | "upAndRight";

const SCALING_FACTOR = 70 / 56;

export interface IChevronProps extends SVGIconPropsV2 {
  direction: Direction;
}

// Implemented using the icons from here: https://faire-wholesale.slack.com/archives/C08PZGMUC3Z/p1753300560248809
export const Chevron = React.memo(
  ({
    direction,
    ref,
    className,
    color = Core.icon.default,
    style,
    size: sizeProp,
    fontSize,
    width,
    height,
    ...props
  }: IChevronProps & React.RefAttributes<SVGElement>) => {
    let size = sizeProp ?? fontSize ?? width ?? height;
    if (size) {
      if (typeof size === "number") {
        size *= SCALING_FACTOR;
      } else if (isRem(size) || isPx(size)) {
        size = pxOrRemToUnitlessPx(size) * SCALING_FACTOR;
      } else if (isIconSize(size)) {
        size = ICON_SIZE_SIZES_PX_UNITLESS[size] * SCALING_FACTOR;
      }
    }
    return (
      <SVGIconV2
        viewBox="0 0 24 24"
        fill="none"
        ref={ref}
        style={{
          ...style,
          transform: getTransform(direction),
        }}
        className={legacyIconCls(className)}
        color={color}
        size={size as SVGIconPropsV2["size"]}
        {...props}
      >
        <path
          d="M8 4L16 12L8 20"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </SVGIconV2>
    );
  }
);

if (process.env.NODE_ENV !== "production") {
  Chevron.displayName = `ChevronIcon`;
}

function getTransform(direction: Direction) {
  switch (direction) {
    case "right":
      return "rotateZ(0deg)";
    case "down":
      return "rotateZ(90deg)";
    case "left":
      return "rotateZ(180deg)";
    case "up":
    default:
      return "rotateZ(270deg)";
  }
}
